import React, { useMemo, useRef } from 'react'

import { IS_EMBEDDED } from '@sketch/constants'

import { useForTablet, ErrorMessage, LoadingState } from '@sketch/components'
import { SharePageCanvas } from '../../PageCanvasView/components/SharePageCanvas'
import PageAnnotationsOverlayContext from '../../PageCanvasView/components/PageAnnotationsOverlayContext'
import PageCanvasViewHeader from '../../PageCanvasView/components/PageCanvasViewHeader'
import { LoadingMessage } from '../../components/LoadingMessage'
import { PageSelectorForPreview } from '../../PageCanvasView/components/PageSelector'

import { PageCanvasSupportGate } from '../../PageCanvasView/components/PageCanvasSupportGate'
import {
  DocumentMobileToolbar,
  isMobileToolboxItem,
} from '../../components/DocumentMobileToolbar'
import {
  CanvasRendererProvider,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'

import { usePlaceDraftAnnotation } from '../../../annotations/hooks'
import ShareReadyGuard from '../../components/ShareReadyGuard'
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-64'
import { AnnotationQueryVariablesProvider } from '../../../annotations/context'
import { DraftAnnotationNotifier } from '../../../annotations/components'

import {
  ErrorWrapper,
  StyledCanvasViewWrapper,
} from '../../PageCanvasView/PageCanvasView.styles'
import { DocumentSidebarLayoutExtraProps } from '../../components/DocumentSidebarLayout'
import {
  useCanvasZoom,
  useIsInvertedZoomDirection,
} from '../../../../components/WebRendererCanvas/hooks'

import { Panel } from '../../components/Panel'
import { FrameInspectorProvider } from '../../FrameView/components/FrameInspectorProvider'
import { useGridAndLayoutPropForZoomComponent } from '../../FrameView'

import { Zoom } from '../../../../components/Zoom'
import {
  MIN_ZOOM,
  MAX_ZOOM,
} from '../../../../components/WebRendererCanvas/constants'
import { INSPECTOR_SEGMENT } from '../../constants'
import { GridsAndLayoutsProvider } from '../../components/GridsAndLayoutsProvider'
import type { GetPreviewDetailsQuery } from '@sketch/gql-types'
import { usePreview } from './PreviewContext'
import {
  CanvasLeftPanel,
  CanvasLeftPanelResizingProvider,
} from '../../../canvas/components/CanvasLeftPanel'
import { useVersioning } from '../../../versioning'
import { useUserSignedIn } from '@sketch/modules-common'

const PageCanvasMobileToolBox = () => {
  const canvasStatus = useStatus()
  const [zoom, setPageZoom] = useCanvasZoom()
  const [placeDraftAnnotation, setPlaceDraftAnnotation] =
    usePlaceDraftAnnotation() || []

  const MOBILE_TOOLBAR_ITEMS = [
    'doc-info',
    'search',
    'zoom',
    'add-comment',
    'versions',
    'comments',
  ].filter(isMobileToolboxItem)

  return (
    <DocumentMobileToolbar
      items={MOBILE_TOOLBAR_ITEMS}
      zoom={zoom}
      setZoom={setPageZoom}
      isDraftAnnotationEnabled={placeDraftAnnotation}
      setPlaceDraftAnnotation={setPlaceDraftAnnotation}
      disableAddAnnotationButton={canvasStatus?.type !== 'READY'}
    />
  )
}

type PageCanvasViewProps = {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
} & DocumentSidebarLayoutExtraProps

const FloatingZoom = () => {
  const [zoom, setZoom] = useCanvasZoom()
  const gridAndLayoutPropsForZoomComponent =
    useGridAndLayoutPropForZoomComponent()
  const preventiveGridAndLayout = gridAndLayoutPropsForZoomComponent

  const [isInvertedZoom, setInvertedZoom] = useIsInvertedZoomDirection()

  return (
    <Zoom
      zoom={zoom}
      setZoom={setZoom}
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
      gridAndLayout={preventiveGridAndLayout}
      explicitControls
      isZoomInverted={isInvertedZoom}
      setIsZoomInverted={setInvertedZoom}
    />
  )
}

export function PageCanvasView(props: PageCanvasViewProps) {
  const {
    HeaderPortal,
    SidebarRightPortal,
    toggleSidebarRight,
    isSidebarRightOpen,
    preview,
  } = props
  const previewContext = usePreview()
  const { currentVersion } = useVersioning()
  const isSignedIn = useUserSignedIn()

  const isInspectPanelOpen = window.location.hash.includes(INSPECTOR_SEGMENT)

  const canvasRef = useRef<HTMLDivElement>(null)

  const isTabletAndBigger = useForTablet()
  const showPagesList = !IS_EMBEDDED

  const firstPageUUID = preview.pages.entries[0]?.uuid
  const permanentPageId =
    previewContext?.currentPage?.uuid || firstPageUUID || ''

  const subjects = useMemo(
    () => [{ type: 'PAGE' as const, permanentId: permanentPageId }],
    [permanentPageId]
  )

  const disableAnnotations = !preview.commentsEnabled || !isSignedIn
  const parsedRenderStatus = 'ready'

  const presentationStatus = preview.presentationStatus ?? 'PENDING'
  const isProcessingPresentationFile = presentationStatus === 'PENDING'

  return (
    <CanvasRendererProvider>
      <CanvasLeftPanelResizingProvider>
        <FrameInspectorProvider
          autoSelect={false}
          //  Reset context for each page
          key={permanentPageId}
        >
          <GridsAndLayoutsProvider>
            <AnnotationQueryVariablesProvider subjects={subjects}>
              <PageAnnotationsOverlayContext
                disabled={disableAnnotations}
                hidden={isInspectPanelOpen}
                isViewingLatestVersion={true}
                permanentPageId={permanentPageId}
              >
                <HeaderPortal>
                  <PageCanvasViewHeader
                    setSidebarLeftOpen={() => {}}
                    toggleSidebarRight={toggleSidebarRight}
                    isSidebarRightOpen={isSidebarRightOpen}
                    isViewingLatestVersion={true}
                    isViewingStarredVersion={false}
                    hasPendingPatches={false}
                    userCanOpenInApp={false}
                    currentVersion={currentVersion}
                  />

                  <Panel
                    userCanSeeComments={
                      (!!previewContext && isSignedIn) || !previewContext
                    }
                    userCanInspect={preview.inspectEnabled}
                    SidebarRightPortal={SidebarRightPortal}
                    version={currentVersion}
                  />
                </HeaderPortal>

                <FloatingZoom />

                <PageCanvasSupportGate>
                  {isProcessingPresentationFile ? (
                    <LoadingState
                      data-testid="loader-preview-canvas"
                      type="color-icon"
                      size="48px"
                      message={<LoadingMessage />}
                    />
                  ) : (
                    <ShareReadyGuard renderStatus={parsedRenderStatus}>
                      {permanentPageId ? (
                        <StyledCanvasViewWrapper>
                          {showPagesList ? (
                            <CanvasLeftPanel
                              isInspectPanelOpen={isInspectPanelOpen}
                              navigationComponent={
                                <PageSelectorForPreview
                                  previewIdentifier={preview.identifier}
                                />
                              }
                            />
                          ) : null}
                          <SharePageCanvas
                            ref={canvasRef}
                            isViewingLatestVersion={true}
                            permanentPageIdentifier={permanentPageId!}
                          />

                          {isTabletAndBigger && <DraftAnnotationNotifier />}
                        </StyledCanvasViewWrapper>
                      ) : (
                        <ErrorWrapper>
                          <ErrorMessage
                            title="No pages in this preview"
                            icon={<DocumentIcon />}
                            iconSize="medium"
                          >
                            No pages have been added here yet.
                          </ErrorMessage>
                        </ErrorWrapper>
                      )}
                    </ShareReadyGuard>
                  )}
                </PageCanvasSupportGate>
                {!IS_EMBEDDED && !isTabletAndBigger && (
                  <PageCanvasMobileToolBox />
                )}
              </PageAnnotationsOverlayContext>
            </AnnotationQueryVariablesProvider>
          </GridsAndLayoutsProvider>
        </FrameInspectorProvider>
      </CanvasLeftPanelResizingProvider>
    </CanvasRendererProvider>
  )
}
